import React, { useState } from 'react';
import { moduleCommonStrings, sharingStrings } from '../../content';

/**
 * Performs the default permission operation.
 *
 * @returns {*} The derived operation result.
 */
function defaultPermission() {
  try {
    return JSON.parse(localStorage.getItem('hobtalk-preferences') || '{}')?.defaultSharingPermission === 'edit'
      ? 'edit'
      : 'view';
  } catch {
    return 'view';
  }
}

/**
 * Retrieves share error message.
 *
 * @param {*} error - Failure being normalized or reported.
 * @returns {*} The derived operation result.
 */
function getShareErrorMessage(error) {
  const message = error?.message || '';
  const normalizedMessage = message.toLowerCase();
  if (
    message.includes('SHARE_RECIPIENT_UNAVAILABLE') ||
    message.includes('USER_PROFILE_NOT_FOUND') ||
    (normalizedMessage.includes('profile') && normalizedMessage.includes('not found')) ||
    normalizedMessage.includes('hobtalk profile')
  ) {
    return sharingStrings.errors.recipeNoProfile;
  }
  if (message.includes('CANNOT_SHARE_WITH_SELF')) {
    return sharingStrings.errors.recipeSelfShare;
  }
  if (message.includes('FORBIDDEN')) {
    return sharingStrings.errors.recipeForbidden;
  }
  return message;
}

/**
 * Renders the share recipe modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.recipe - recipe value.
 * @param {*} props.shares - shares value.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @param {*} props.onShare - Callback invoked when share occurs.
 * @param {*} props.onRevoke - Callback invoked when revoke occurs.
 * @param {*} props.isSharing - Whether is sharing is enabled.
 * @param {*} props.isRevoking - Whether is revoking is enabled.
 * @param {*} props.error - Failure being normalized or reported.
 * @param {*} props.successMessage - success message value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function ShareRecipeModal({
  recipe,
  shares = [],
  onClose,
  onShare,
  onRevoke,
  isSharing,
  isRevoking,
  error,
  successMessage,
}) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState(defaultPermission);
  const [localError, setLocalError] = useState('');
  const [pendingRevoke, setPendingRevoke] = useState(null);

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email.trim()) {
      setLocalError(sharingStrings.errors.emailRequired);
      return;
    }
    onShare({ granteeEmail: email.trim(), permission });
    setEmail('');
  };

  const revokeLabel = pendingRevoke?.granteeDisplayName || pendingRevoke?.granteeEmail || '';

  return (
    <div className="modal-overlay">
      <form className="modal-content share-modal" onSubmit={handleSubmit} noValidate>
        <div className="section-heading compact-heading">
          <div>
            <p className="eyebrow">{sharingStrings.recipe.eyebrow}</p>
            <h2>{recipe.name}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}>{moduleCommonStrings.actions.close}</button>
        </div>

        <div className="form-grid two-columns">
          <div className="form-group">
            <label>
              <span>{sharingStrings.fields.recipientEmail}</span>
              <em>{sharingStrings.fields.required}</em>
            </label>
            <input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setLocalError('');
              }}
              placeholder={sharingStrings.fields.emailPlaceholder}
              aria-invalid={Boolean(localError)}
            />
            {localError && <p className="field-error">{localError}</p>}
          </div>
          <div className="form-group">
            <label>
              <span>{sharingStrings.fields.permission}</span>
              <em>{sharingStrings.fields.required}</em>
            </label>
            <select value={permission} onChange={(event) => setPermission(event.target.value)}>
              <option value="view">{sharingStrings.recipe.permissions.view}</option>
              <option value="edit">{sharingStrings.recipe.permissions.edit}</option>
            </select>
          </div>
        </div>

        <p className="share-helper">
          {sharingStrings.recipe.helper}
        </p>
        {successMessage && <p className="success-copy">{successMessage}</p>}
        {error && <p className="field-error">{getShareErrorMessage(error)}</p>}

        <div className="form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>{moduleCommonStrings.actions.cancel}</button>
          <button type="submit" className="btn-primary" disabled={isSharing}>
            {isSharing ? sharingStrings.recipe.sharing : sharingStrings.recipe.action}
          </button>
        </div>

        <div className="share-list">
          <h3>{sharingStrings.recipe.peopleWithAccess}</h3>
          {shares.map((share) => {
            const isPending = share.status === 'pending' || !share.granteeUserId;
            return (
              <div key={share.id} className="share-list-row">
              <div>
                <strong>{share.granteeDisplayName || share.granteeEmail}</strong>
                <span>
                  {isPending ? sharingStrings.recipe.pending : share.granteeEmail}
                  {' - '}
                  {share.permission === 'edit' ? sharingStrings.recipe.permissions.edit : sharingStrings.recipe.permissions.view}
                </span>
              </div>
              <button type="button" className="row-remove-btn" disabled={isRevoking} onClick={() => setPendingRevoke(share)}>
                {moduleCommonStrings.actions.remove}
              </button>
            </div>
            );
          })}
          {shares.length === 0 && <p className="muted-copy">{sharingStrings.recipe.empty}</p>}
        </div>
      </form>
      {pendingRevoke && (
        <div className="modal-overlay nested-modal-overlay">
          <div className="modal-content delete-confirmation">
            <p className="eyebrow">{sharingStrings.recipe.peopleWithAccess}</p>
            <h2>{sharingStrings.revoke.title}</h2>
            <p>{sharingStrings.revoke.detail(revokeLabel)}</p>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setPendingRevoke(null)}>
                {moduleCommonStrings.actions.cancel}
              </button>
              <button
                type="button"
                className="btn-danger solid"
                disabled={isRevoking}
                onClick={() => {
                  onRevoke(pendingRevoke.id);
                  setPendingRevoke(null);
                }}
              >
                {isRevoking ? sharingStrings.revoke.removing : sharingStrings.revoke.action}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
